import { ResponseDelay } from 'types/rest/mockServiceTypes';
import { TransactionType } from './assetCatalogTypes';

export interface GenericDsl {
  type: TransactionType;
  requestDsl: RequestDsl | MsgRequestDsl;
  responseDsl: ResponseDsl | MsgResponseDsl;
  redirectUrl?: string;
  certificateId?: number;
  priority?: number;
}

export interface HttpGenericDsl extends GenericDsl {
  requestDsl: RequestDsl;
  responseDsl: ResponseDsl;
}

export interface MsgGenericDsl extends GenericDsl {
  requestDsl: MsgRequestDsl;
  responseDsl: MsgResponseDsl;
}

export interface Condition {
  matcher: MatcherDsl;
}

export interface StatusCodeCondition extends Condition {
  status: number;
  statusMessage: string;
}

export type FaultType =
  | 'EMPTY_RESPONSE'
  | 'RANDOM_DATA_THEN_CLOSE'
  | 'MALFORMED_RESPONSE_CHUNK'
  | 'CONNECTION_RESET_BY_PEER';

export interface ResponseDsl {
  status: number;
  statusMessage: string;
  statusCodeConditions?: StatusCodeCondition[];
  proxyUrl: string;
  headers?: HttpKeyValue[];
  contentType: ContentType;
  content: string;
  binary: boolean;
  responseDelay?: ResponseDelay;
  failoverEnabled?: boolean;
  fault?: FaultType;
}

export interface MsgResponseDsl {
  messageType: string;
  contentType: ContentType;
  content: string;
  headers?: HttpKeyValue[];
  properties?: HttpKeyValue[];
  binary: boolean;
  charset: string;
  responseDelay?: ResponseDelay;
  failoverEnabled?: boolean;
}

export interface Namespace {
  prefix: string;
  uri: string;
}

export enum Matcher {
  any = 'any',
  equals_url = 'equals_url',
  matches_url = 'matches_url',
  equals = 'equals',
  equals_insensitive = 'equals_insensitive',
  equals_binary = 'equals_binary',
  contains = 'contains',
  matches = 'matches',
  not_matches = 'not_matches',
  equals_json = 'equals_json',
  matches_json = 'matches_json',
  equals_xml = 'equals_xml',
  matches_xml = 'matches_xml',
  matches_xml_cdata = 'matches_xml_cdata',
  matches_xml_schema = 'matches_xml_schema',
  matches_graphql_body = 'matches_graphql_body',
  matches_graphql_schema = 'matches_graphql_schema',
  not_matches_graphql_schema = 'not_matches_graphql_schema',
  absent = 'absent',
  greater_than = 'greater_than',
  less_than = 'less_than',
}

export enum MessageType {
  BYTES_MESSAGE = 'BYTES_MESSAGE',
  TEXT_MESSAGE = 'TEXT_MESSAGE',
  STREAM_MESSAGE = 'STREAM_MESSAGE',
  MAP_MESSAGE = 'MAP_MESSAGE',
  OBJECT_MESSAGE = 'OBJECT_MESSAGE',
}
export type MessageTypeList = { [key in MessageType]?: string };

export const messageTypes: MessageTypeList = {
  BYTES_MESSAGE: 'Bytes Message',
  TEXT_MESSAGE: 'Text Message',
  STREAM_MESSAGE: 'Stream Message',
  MAP_MESSAGE: 'Map Message',
  OBJECT_MESSAGE: 'Object Message',
};

export enum MessagePropertyType {
  BOOLEAN = 'BOOLEAN',
  BYTE = 'BYTE',
  SHORT = 'SHORT',
  INT = 'INT',
  LONG = 'LONG',
  FLOAT = 'FLOAT',
  DOUBLE = 'DOUBLE',
  STRING = 'STRING',
}
export type MessagePropertyTypeList = { [key in MessagePropertyType]?: string };
export const messagePropertyTypes: MessagePropertyTypeList = {
  BOOLEAN: 'Boolean',
  BYTE: 'Byte',
  SHORT: 'Short',
  INT: 'Int',
  LONG: 'Long',
  FLOAT: 'Float',
  DOUBLE: 'Double',
  STRING: 'String',
};

export interface MatcherDsl {
  key: string;
  matcherName: Matcher;
  matchingValue: string;
  cdataXpath?: string | null;
  sampleBody?: string | null;
  optional?: boolean;
  nextMatcher?: MatcherDsl;
  namespaces?: Namespace[];
}

export interface Credentials {
  username: string;
  password: string;
}

export interface RequestDsl {
  method: string;
  host: string;
  path: string;
  url: MatcherDsl;
  headers?: MatcherDsl[];
  queryParams?: MatcherDsl[];
  cookies?: MatcherDsl[];
  credentials?: Credentials;
  body?: MatcherDsl[];
}

export interface MsgRequestDsl {
  headers?: MatcherDsl[];
  properties?: MatcherDsl[];
  body?: MatcherDsl[];
}

export interface HttpKeyValue {
  name: string;
  value: string;
  type?: MessagePropertyType;
}

export interface ErrorFields {
  fieldType: string;
  values: string;
  errorMessage: string;
}

export type ContentType = 'json' | 'xml' | 'html' | 'text' | 'binary';
